import { dates } from "@omegagrid/core";
import { getLocale } from "@omegagrid/localize";
import { Accounting, User, AccountingGroup } from "@reporting/rp-types/core";
import { AccessRole } from "@reporting/rp-types/users";

export type ObjectFormatOptions<T> = {
	key?: keyof T;
	value?: keyof T;
	format?: (k: T[keyof T], v: T[keyof T]) => string;
};

const DEFAULT_FORMAT_FN = <T>(k: T[keyof T], v: T[keyof T]) => [k, v].filter(x => x != null && x != '').join(' ');

export function formatObject<T>(object: T|null, options: ObjectFormatOptions<T>): string {
	if (!object) return '';
	const fn = options.format || DEFAULT_FORMAT_FN;
	return fn(object[options.key], object[options.value]);
}

export function formatUser(user: User|null, options?: ObjectFormatOptions<User>): string {
	return formatObject(user, {...{
		key: user.user_name != user.login_name ? 'user_name' : undefined,
		value: 'login_name'
	}, ...options});
}

export function formatAccessRole(role: AccessRole|null, options?: ObjectFormatOptions<AccessRole>): string {
	return formatObject(role, {...{key: 'id_access_role', value: 'role'}, ...options});
}

export function formatAccounting(accounting: Accounting|null, options?: ObjectFormatOptions<Accounting>): string {
	return formatObject(accounting, {...{key: 'accounting', value: 'company'}, ...options});
}

export function formatAccountingGroup(group: AccountingGroup|null, options?: ObjectFormatOptions<AccountingGroup>): string {
	return formatObject(group, {...{key: 'id_group', value: 'group_name'}, ...options});
}

export function formatBytes(bytes: number|null, decimals = 2): string {
	if (bytes == null || isNaN(bytes)) return '';
	if (bytes === 0) return '0 B';
	const k = 1024;
	const dm = decimals < 0 ? 0 : decimals;
	const sizes = ['B', 'KB', 'MB', 'GB', 'TB', 'PB', 'EB', 'ZB', 'YB'];
	const i = Math.floor(Math.log(bytes) / Math.log(k));
	return parseFloat((bytes / Math.pow(k, i)).toFixed(dm)) + ' ' + sizes[i];
}

export function formatSeconds(seconds: number|null): string {
	if (seconds == null || isNaN(seconds)) return '';
	const s = Math.floor(seconds);
	const h = Math.floor(s / 3600);
	const m = Math.floor(s / 60) % 60;
	const sec = s % 60;
	const pad = (n: number) => n.toString().padStart(2, '0');
	return `${pad(h)}:${pad(m)}:${pad(sec)}`;
}

export function formatDateTime(d: string | number | Date, format?: string, isSerialDate?: boolean): string {
	if (!d) return '!invalid date';
	const dt = isSerialDate && typeof d === 'number' ? dates.serialDateToDate(d) : new Date(d);
	return dates.format(dt, format || dates.getDateFormat({ withTime: true }));
};

export function formatDate(d: string | number | Date, format?: string, isSerialDate?: boolean): string {
	if (!d) return '!invalid date';
	const dt = isSerialDate && typeof d === 'number' ? dates.serialDateToDate(d) : new Date(d);
	return dates.format(dt, format || dates.getDateFormat({ withTime: false }));
};

export function toIsoDate(value: Date | string | number | null | undefined): string | null {
	if (value == null || value === '') return null;
	if (typeof value === 'number') {
		// a serial date counts the days, serialDateToDate() gives its midnight in UTC
		return Number.isFinite(value) ? dates.serialDateToDate(value).toISOString().slice(0, 10) : null;
	}
	if (typeof value === 'string' && dates.isISODate(value)) return value.slice(0, 10);
	const date = typeof value === 'string' ? dates.parse(value, getLocale()) : value;
	if (!(date instanceof Date) || isNaN(date.getTime())) return null;
	const pad = (n: number) => n.toString().padStart(2, '0');
	return `${date.getFullYear()}-${pad(date.getMonth() + 1)}-${pad(date.getDate())}`;
}

export function fromIsoDate(value: string | null | undefined): Date | null {
	const iso = toIsoDate(value);
	if (!iso) return null;
	const [y, m, d] = iso.split('-').map(Number);
	return new Date(y, m - 1, d);
}
